// ManualUpdateTab.jsx (new file)
import React, { useState, useCallback, useEffect, useRef } from "react";
import { alpha, Grid, Skeleton, styled, Switch, Tooltip } from "@mui/material";
import DataTable from "../../../Components/Common/datatable/DataTable";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";
import ApiService from "../../../utils/fetch";
import EditIcon from "@mui/icons-material/Edit";
import ButtonComponent from "../../../Components/Common/button/Button";
import { safeTimestamp } from "../../../Components/Common/DateFormatter";
import BlackWhiteSwitch from "../../BottlerForecast/MaterialPlaning/BlackWhiteSwitch";

const PRManualUpdate = ({ activeTab }) => {
  const [manualData, setManualData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  useEffect(() => {
    if (activeTab === 1) {
      manualUpdateData();
    }
  }, [activeTab]);

  const manualUpdateData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get("/v1/custom-pr/get-details");
      setManualData(response.data);
    } catch (error) {
      console.error("Error posting data", error);
    } finally {
      setLoading(false);
    }
  };

  const onGrid = useCallback((params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  }, []);

  useEffect(() => {
    const resizeGrid = () => {
      if (gridRef.current && gridRef.current.api && manualData.length > 0) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    resizeGrid();

    window.addEventListener("resize", resizeGrid);
    return () => window.removeEventListener("resize", resizeGrid);
  }, [manualData]);

  const onCellValueChanged = (params) => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({ force: true });
      gridRef.current.api.sizeColumnsToFit();
    }
    if (!params.node.isSelected()) {
      params.node.setSelected(true);
    }
    setHasUnsavedChanges(true);
  };

  const onSelectionChanged = useCallback((event) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  }, []);

  const getColumnRowStyle = useCallback((params) => {
    if (params.node?.isSelected?.()) {
      return { border: "1.5px solid red", borderRadius: "4px" };
    }
    return {};
  }, []);

  const manualUpdateSave = async () => {
    const invalidRows = selectedRows.some(
      (row) => row.bottlerKit == null || row.bottlerKit === "",
    );

    if (invalidRows) {
      setSelectedRows([]);
      setHasUnsavedChanges(false);

      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({ force: true });
        gridRef.current.api.deselectAll();
      }

      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Please ensure Bottler Kit field is filled.",
      });
      return;
    }

    selectedRows.forEach((selected) => {
      const match = manualData.find(
        (item) =>
          item.customerCountry === selected.customerCountry &&
          item.bottler === selected.bottler &&
          item.bottlerOwnerCode === selected.bottlerOwnerCode,
      );

      if (match) {
        match.bottlerKit = selected.bottlerKit;
      }
    });

    const hasNullBottlerKitId = manualData.some(
      (item) => item.bottlerKit === null,
    );

    if (hasNullBottlerKitId) {
      await MessagePopup({
        icon: "info",
        title: "Unmapped Rows Detected",
        html: "<p>It is recommended to map all rows before saving.</p>",
        confirmButtonText: "OK",
        confirmButtonColor: "#000000",
      });
    }

    const result = await MessagePopup({
      title: "Confirm Save",
      html: "<p>Are you sure you want to save the configuration?</p>",
      showCancelButton: true,
      confirmButtonText: "Ok",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) return;

    if (selectedRows.length === 0) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Please select at least one row to save.",
      });
      return;
    }

    const requestBody = selectedRows.map((row) => ({
      customerCountry: row.customerCountry,
      bottler: row.bottler,
      bottlerOwnerCode: row.bottlerOwnerCode,
      bottlerKitId: row.bottlerKitId,
      productId: row.productId,
      cpsKit: row.cpsKit,
      unitOfMeasure: row.unitOfMeasure,
      bottlerKit: row.bottlerKit,
      mappingStatus: row.mappingStatus,
      updatedBy: row.updatedBy,
      updatedDate: row.updatedDate,
      // beverageProductCode: row.beverageProductCode,
      // beverageProductDescription: row.beverageProductDescription,
    }));

    try {
      await ApiService.post("/v1/custom-pr/manual-update", requestBody);

      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Updated successfully.",
      });

      setHasUnsavedChanges(false);

      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({ force: true });
        gridRef.current.api.deselectAll();
      }

      setSelectedRows([]);
      manualUpdateData();
    } catch (error) {
      MessagePopup({
        icon: "error",
        title: "Error",
        message: "Failed to update. Please try again.",
      });
    }
  };

  function dateComparator(date1, date2) {
    // Convert both ISO 8601 strings to timestamps (milliseconds since Unix Epoch)
    const date1Number = _isoDateToTimestamp(date1);
    const date2Number = _isoDateToTimestamp(date2);

    // Handle null or invalid dates
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    // Compare timestamps
    return date1Number - date2Number;
  }

  function _isoDateToTimestamp(isoDate) {
    if (!isoDate || typeof isoDate !== "string") {
      return null;
    }

    const date = new Date(isoDate);
    // Check if Date parsing failed
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  }

  const ColumnDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
      hide: true,
    },
    {
      field: "bottler",
      headerName: "Bottler",
      width: 160,
      minWidth: 160,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "bottlerOwnerCode",
      headerName: "Bottler Owner Code",
      sortable: true,
      unSortIcon: true,
      width: 230,
      minWidth: 230,
      valueGetter: (params) => {
        const { bottlerOwnerCode, bottlerOwnerDescription } = params.data;
        return `${bottlerOwnerCode || ""}-${bottlerOwnerDescription || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "customerCountry",
      headerName: "Customer Country",
      width: 200,
      minWidth: 200,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "cpsKit",
      headerName: "CPS Kit",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      valueGetter: (params) => {
        const { cpsKit } = params.data;
        return `${cpsKit || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "beverageProductCode",
      headerName: "Beverage Product Code",
      width: 260,
      minWidth: 260,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "beverageProductDescription",
      headerName: "Beverage Product Description",
      width: 300,
      minWidth: 300,
      sortable: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (p) => (
        <Tooltip title={p.value || ""} arrow>
          <div>{p.value}</div>
        </Tooltip>
      ),
    },
    {
      field: "unitOfMeasure",
      headerName: "Unit of Measure",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.unitOfMeasure}`} arrow>
          <div>{params.data.unitOfMeasure}</div>
        </Tooltip>
      ),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "bottlerKit",
      headerName: "Bottler Kit",
      sortable: true,
      unSortIcon: true,
      width: 210,
      minWidth: 210,
      editable: true,
      cellClass: "cpsComments-cell",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => {
        return (
          <div className="cps-comments-icon">
            <span style={{ flex: 1, padding: "0px 4px" }}>{params.value} </span>
            <EditIcon fontSize="sm" />
          </div>
        );
      },
    },
    {
      field: "mappingStatus",
      headerName: "Mapping Status",
      sortable: true,
      unSortIcon: true,
      cellClass: (params) => (params.value === false ? "red-cell" : ""),
      width: 200,
      minWidth: 200,
      filter: "agMultiColumnFilter",
      sort: "asc",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
      cellRenderer: (params) => {
        const isChecked = params.value === true;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BlackWhiteSwitch checked={isChecked} disabled={true} />
          </div>
        );
      },
    },
    {
      field: "updatedBy",
      headerName: "Updated By",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,
      cellRenderer: (params) => (
        <Tooltip title={`${params.data.updatedBy}`} arrow>
          <div>{params.data.updatedBy}</div>
        </Tooltip>
      ),
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "updatedDate",
      headerName: "Updated Date(UTC)",
      sortable: true,
      unSortIcon: true,
      width: 250,
      minWidth: 250,
      filterParams: {
        comparator: function (filterDate, cellValue) {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          // Strip time and compare only YYYY-MM-DD
          const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate(),
          );

          return cellDateOnly.getTime() - filterDate.getTime();
        },
        filterOptions: ["equals", "inRange"],
        defaultOption: "equals",
        buttons: ["apply", "reset"],
        closeOnApply: true,
        onApply: (params) => params.api.hidePopupMenu(),
        onReset: (params) => params.api.hidePopupMenu(),
      },
      filter: "agDateColumnFilter",
      comparator: dateComparator,
      valueFormatter: (params) => {
        return safeTimestamp(params.value);
      },
    },
  ];

  return (
    <div id="manualUpdatePage">
      <style>{`
        #manualUpdatePage .ag-theme-quartz {
          --ag-active-color: black; /* White for active/selected states */
        }
      `}</style>
      <Grid
        container
        spacing={1}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Grid
          item
          style={{ display: "flex", alignItems: "center", color: "#4d4d4d" }}
        >
          Additional kits can be added through the Excel update
        </Grid>
        <Grid item xs={1}>
          <ButtonComponent
            label="Save"
            bgColor="black"
            color="white"
            onClick={manualUpdateSave}
            fullWidth={false}
            disabled={!hasUnsavedChanges}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={"75vh"}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={gridRef}
              columns={ColumnDefinition}
              rows={manualData || []}
              tableHeight={"75vh"}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              onGridReady={onGrid}
              rowSelection="multiple"
              getRowStyle={getColumnRowStyle}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={onSelectionChanged}
              singleClickEdit={true}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default PRManualUpdate;
